import { FC, useEffect } from 'react';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	activatePrpRequest,
	resetPrpSettingsSaveSuccess,
	updatePrpSettingsRequest,
} from 'redux/reducers/prp/reducer';
import { getPrpSettingsSaveLoading, getPrpSettingsSaveSuccess } from 'redux/reducers/prp/selectors';
import { TPrpSettingsDialogProps, TPrpSettingsFormValues } from './types';

const validationSchema = Yup.object({
	representationFee: Yup.number().required('Representation fee is required').min(0, 'Must be >= 0'),
	pddpApiKey: Yup.string().test(
		'min-length',
		'PDDP API Key must be at least 10 characters',
		(value) => !value?.trim() || value.trim().length >= 10,
	),
});

export const PrpSettingsDialog: FC<TPrpSettingsDialogProps> = ({
	open,
	onClose,
	emId,
	mode,
	settings,
}) => {
	const dispatch = useDispatch();
	const isEdit = mode === 'edit';

	const loading = useSelector(getPrpSettingsSaveLoading);
	const saveSuccess = useSelector(getPrpSettingsSaveSuccess);

	const formik = useFormik<TPrpSettingsFormValues>({
		initialValues: {
			representationFee: settings?.representationFee ?? ('' as unknown as number),
			prpEnabled: settings?.prpEnabled ?? true,
			pddpApiKey: '',
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const pddpApiKey = values.pddpApiKey.trim() || null;
			if (isEdit) {
				dispatch(
					updatePrpSettingsRequest({
						emId,
						body: {
							representationFee: Number(values.representationFee),
							prpEnabled: values.prpEnabled,
							pddpApiKey,
						},
					}),
				);
			} else {
				dispatch(
					activatePrpRequest({
						emId,
						body: {
							representationFee: Number(values.representationFee),
							pddpApiKey,
						},
					}),
				);
			}
		},
	});

	useEffect(() => {
		if (saveSuccess) {
			dispatch(resetPrpSettingsSaveSuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveSuccess]);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	const showPddpApiKeyError =
		!!formik.errors.pddpApiKey && (formik.touched.pddpApiKey || !!formik.values.pddpApiKey.trim());

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{isEdit ? 'Edit PRP Settings' : 'Activate PRP'}</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Representation Fee"
						name="representationFee"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.representationFee}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.representationFee && !!formik.errors.representationFee}
						helperText={formik.touched.representationFee && formik.errors.representationFee}
					/>
					<TextField
						label="PDDP API Key"
						name="pddpApiKey"
						size="small"
						placeholder={
							settings?.hasPddpApiKey ? 'A key is already set — leave blank to clear it' : ''
						}
						value={formik.values.pddpApiKey}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={showPddpApiKeyError}
						helperText={showPddpApiKeyError && formik.errors.pddpApiKey}
					/>
					{isEdit && (
						<FormControlLabel
							control={
								<Checkbox
									name="prpEnabled"
									checked={formik.values.prpEnabled}
									onChange={formik.handleChange}
								/>
							}
							label="PRP Enabled"
						/>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading || formik.values.pddpApiKey.trim().length < 10}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Activate'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
